import { useState, useEffect } from 'react'
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useGame } from '../../contexts/GameContext'
import { useTournaments } from '../../contexts/TournamentContext'
import { useAudio } from '../../contexts/AudioContext'
import { useWebSocket } from '../../contexts/WebSocketContext'
import UserDisplay from '../UserDisplay/UserDisplay'
import QuestAccountModal from '../Modal/QuestAccountModal'
import SoundToggle from '../SoundToggle/SoundToggle'

export default function Layout() {
  const { user, profile: authProfile, signOut, isGuest, triggerRegistration, session } = useAuth()
  const { getProfile } = useGame()
  const { listTournaments } = useTournaments() // Keep tournaments loaded for notifications/badges if needed
  const { isConnected } = useWebSocket()
  const navigate = useNavigate()
  const location = useLocation()

  const [activeDropdown, setActiveDropdown] = useState(null)
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0)
  const [isAdmin, setIsAdmin] = useState(false)
  const [profileStats, setProfileStats] = useState(null)
  const [showQuestModal, setShowQuestModal] = useState(false)

  // Load profile stats
  useEffect(() => {
    let isMounted = true
    const loadProfile = async () => {
      if (!user) {
        setProfileStats(null)
        return
      }
      try {
        let profileData = authProfile
        if (!profileData && session?.access_token) {
           profileData = await getProfile()
        }
        
        if (isMounted) {
          setProfileStats(profileData)
        }
      } catch (err) {
        console.error('Failed to load profile:', err)
      }
    }
    loadProfile()
    return () => {
      isMounted = false
    }
  }, [user?.id, authProfile, session?.access_token, getProfile])

  // Admin Check
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user || isGuest) {
        setIsAdmin(false)
        return
      }

      try {
        const API_BASE = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE
        const authData = JSON.parse(localStorage.getItem('auth'))

        if (!authData?.session?.access_token) return

        const response = await fetch(`${API_BASE}/admin/check`, {
          headers: {
            'Authorization': `Bearer ${authData.session.access_token}`
          }
        })

        if (response.ok) {
          const data = await response.json()
          setIsAdmin(data.is_admin || false)
        }
      } catch (error) {
        setIsAdmin(false)
      }
    }
    checkAdminStatus()
  }, [user, isGuest])

  // Notification Count
  const fetchUnreadNotificationCount = async () => {
    if (!user || isGuest) {
      setUnreadNotificationCount(0)
      return
    }

    try {
      const API_BASE = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE
      const authData = JSON.parse(localStorage.getItem('auth'))

      if (!authData?.session?.access_token) return

      const response = await fetch(`${API_BASE}/notifications/count`, {
        headers: {
          'Authorization': `Bearer ${authData.session.access_token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setUnreadNotificationCount(data.count || 0)
      }
    } catch (error) {
      console.error('Failed to fetch notification count:', error)
    }
  }

  useEffect(() => {
    fetchUnreadNotificationCount()
    // Poll for notifications every minute
    const interval = setInterval(fetchUnreadNotificationCount, 60000)
    return () => clearInterval(interval)
  }, [user, isGuest])

  // Global event listener for notification updates (from other components)
  useEffect(() => {
    const handleRefreshNotifications = () => fetchUnreadNotificationCount()
    window.addEventListener('refresh-notifications', handleRefreshNotifications)
    return () => window.removeEventListener('refresh-notifications', handleRefreshNotifications)
  }, [])


  // Scroll shadow effect
  useEffect(() => {
    const handleScroll = () => {
      const header = document.querySelector('.dashboard-header')
      if (!header) return
      if (window.scrollY > 50) {
        header.classList.add('scrolled')
      } else {
        header.classList.remove('scrolled')
      }
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Scroll to top on navigation
  useEffect(() => {
    window.scrollTo(0, 0)
    setShowMobileMenu(false)
    setActiveDropdown(null)
  }, [location.pathname])

  const handleSignOut = async () => {
    await signOut()
    navigate('/')
  }

  const handleNavigateToAuth = () => {
    setShowQuestModal(false)
    triggerRegistration()
  }

  const displayName = profileStats?.username || 'Loading...'
  
  // Helper to check active route
  const isActive = (path) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <Link to="/" className="logo" onClick={() => setActiveDropdown(null)}>
              <img
                src="/images/logo/riftchesslogohorizontalfinal.png"
                alt="RiftChess"
                className="logo-horizontal"
              />
              <img
                src="/images/logo/finallogosquare.png"
                alt="RiftChess"
                className="logo-icon"
              />
            </Link>
          </div>

          <div className="header-center">
            <nav className="main-nav-dropdown">
              {/* Home Link */}
              <Link
                to="/"
                className={`nav-home-link ${isActive('/') ? 'active' : ''}`}
                onClick={() => setActiveDropdown(null)}
              >
                Home
              </Link>

              {/* Game Dropdown */}
              <div
                className="nav-dropdown-container"
                onMouseEnter={() => setActiveDropdown('game')}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <button
                  className={`nav-dropdown-trigger ${(['/tournaments', '/leaderboard', '/replay', '/spectate'].some(p => location.pathname.startsWith(p)) || activeDropdown === 'game') ? 'active' : ''}`}
                  onClick={() => setActiveDropdown(activeDropdown === 'game' ? null : 'game')}
                >
                  Game
                  <span className="dropdown-arrow">▼</span>
                </button>
                {activeDropdown === 'game' && (
                  <div className="nav-dropdown-menu">
                    <div className="dropdown-section">
                      <div className="dropdown-section-title">Compete</div>
                      <Link to="/tournaments" className="dropdown-item" onClick={() => setActiveDropdown(null)}>
                        <div className="dropdown-item-content">
                          <span className="dropdown-item-title">Tournaments</span>
                          <span className="dropdown-item-desc">Competitive matches</span>
                        </div>
                      </Link>
                      <Link to="/leaderboard" className="dropdown-item" onClick={() => setActiveDropdown(null)}>
                        <div className="dropdown-item-content">
                          <span className="dropdown-item-title">Leaderboard</span>
                          <span className="dropdown-item-desc">Rankings and stats</span>
                        </div>
                      </Link>
                      <Link to="/replay" className="dropdown-item" onClick={() => setActiveDropdown(null)}>
                        <div className="dropdown-item-content">
                          <span className="dropdown-item-title">Replay</span>
                          <span className="dropdown-item-desc">Watch and analyze games</span>
                        </div>
                      </Link>
                      <Link to="/spectate" className="dropdown-item" onClick={() => setActiveDropdown(null)}>
                        <div className="dropdown-item-content">
                          <span className="dropdown-item-title">Spectate</span>
                          <span className="dropdown-item-desc">Watch live games</span>
                        </div>
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              {/* Social Dropdown */}
              <div
                className="nav-dropdown-container"
                onMouseEnter={() => setActiveDropdown('social')}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <button
                  className={`nav-dropdown-trigger ${(['/friends', '/teams', '/quests'].some(p => location.pathname.startsWith(p)) || activeDropdown === 'social') ? 'active' : ''}`}
                  onClick={() => setActiveDropdown(activeDropdown === 'social' ? null : 'social')}
                >
                  Social
                  <span className="dropdown-arrow">▼</span>
                </button>
                {activeDropdown === 'social' && (
                  <div className="nav-dropdown-menu">
                    <div className="dropdown-section">
                      <div className="dropdown-section-title">Community</div>
                      <Link to="/friends" className="dropdown-item" onClick={() => setActiveDropdown(null)}>
                        <div className="dropdown-item-content">
                          <span className="dropdown-item-title">Friends</span>
                          <span className="dropdown-item-desc">Connect with players</span>
                        </div>
                      </Link>
                      <Link to="/teams" className="dropdown-item" onClick={() => setActiveDropdown(null)}>
                        <div className="dropdown-item-content">
                          <span className="dropdown-item-title">Teams</span>
                          <span className="dropdown-item-desc">Join or create teams</span>
                        </div>
                      </Link>
                      <Link to="/quests" className="dropdown-item" onClick={() => setActiveDropdown(null)}>
                        <div className="dropdown-item-content">
                          <span className="dropdown-item-title">Quests</span>
                          <span className="dropdown-item-desc">Complete challenges</span>
                        </div>
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              {/* Learn Dropdown */}
              <div
                className="nav-dropdown-container"
                onMouseEnter={() => setActiveDropdown('learn')}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <button
                  className={`nav-dropdown-trigger ${(['/why-riftchess', '/rules'].some(p => location.pathname.startsWith(p)) || activeDropdown === 'learn') ? 'active' : ''}`}
                  onClick={() => setActiveDropdown(activeDropdown === 'learn' ? null : 'learn')}
                >
                  Learn
                  <span className="dropdown-arrow">▼</span>
                </button>
                {activeDropdown === 'learn' && (
                  <div className="nav-dropdown-menu">
                    <div className="dropdown-section">
                      <div className="dropdown-section-title">Resources</div>
                      <Link to="/why-riftchess" className="dropdown-item" onClick={() => setActiveDropdown(null)}>
                        <div className="dropdown-item-content">
                          <span className="dropdown-item-title">What is RiftChess?</span>
                          <span className="dropdown-item-desc">Introduction to the game</span>
                        </div>
                      </Link>
                      <Link to="/rules" className="dropdown-item" onClick={() => setActiveDropdown(null)}>
                        <div className="dropdown-item-content">
                          <span className="dropdown-item-title">Rules</span>
                          <span className="dropdown-item-desc">Game rules and mechanics</span>
                        </div>
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </nav>
          </div>

          <button
            className="mobile-menu-toggle"
            onClick={() => setShowMobileMenu(!showMobileMenu)}
          >
            ☰
          </button>

          <div className="header-right">
            <nav className="account-nav">
              <div
                className="nav-dropdown-container"
                onMouseEnter={() => setActiveDropdown('account')}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <button
                  className={`nav-dropdown-trigger ${(['/profile', '/preferences', '/notifications'].some(p => location.pathname.startsWith(p)) || activeDropdown === 'account') ? 'active' : ''}`}
                  onClick={() => setActiveDropdown(activeDropdown === 'account' ? null : 'account')}
                >
                  <div className="account-trigger-content">
                    <div className="profile-avatar-small">
                      <span>{displayName?.charAt(0).toUpperCase()}</span>
                      {unreadNotificationCount > 0 && (
                        <div className="notification-indicator">
                          {unreadNotificationCount > 9 ? '9+' : unreadNotificationCount}
                        </div>
                      )}
                    </div>
                    <div className="account-text">
                      <div className="account-username">
                        <span>{displayName}</span>
                      </div>
                    </div>
                  </div>
                  <span className="dropdown-arrow">▼</span>
                </button>
                {activeDropdown === 'account' && (
                  <div className="nav-dropdown-menu account-dropdown-menu">
                    <div className="dropdown-section">
                      <div className="dropdown-section-title">Settings</div>
                      <div className="dropdown-item" onClick={() => {
                        const username = profileStats?.username || user?.user_metadata?.username || user?.email?.split('@')[0];
                        if (username) window.open(`/user/${username}`, '_blank');
                        setActiveDropdown(null);
                      }}>
                        <div className="dropdown-item-content">
                          <span className="dropdown-item-title">Public Profile ↗</span>
                          <span className="dropdown-item-desc">View your public profile</span>
                        </div>
                      </div>
                      <Link to="/profile" className="dropdown-item" onClick={() => setActiveDropdown(null)}>
                        <div className="dropdown-item-content">
                          <span className="dropdown-item-title">Profile Settings</span>
                          <span className="dropdown-item-desc">Account and preferences</span>
                        </div>
                      </Link>
                      <Link to="/preferences" className="dropdown-item" onClick={() => setActiveDropdown(null)}>
                        <div className="dropdown-item-content">
                          <span className="dropdown-item-title">Game Preferences</span>
                          <span className="dropdown-item-desc">Display and controls</span>
                        </div>
                      </Link>
                    </div>
                    <div className="dropdown-section">
                      <div className="dropdown-section-title">Notifications</div>
                      <Link to="/notifications" className="dropdown-item" onClick={() => setActiveDropdown(null)}>
                        <div className="dropdown-item-content">
                          <span className="dropdown-item-title">Notifications</span>
                          <span className="dropdown-item-desc">Updates and alerts</span>
                        </div>
                      </Link>
                    </div>
                    {profileStats?.account_type !== 'premium' && (
                      <div className="dropdown-section">
                        <div className="dropdown-section-title">Premium</div>
                        <Link to="/premium" className="dropdown-item" onClick={() => setActiveDropdown(null)}>
                          <div className="dropdown-item-content">
                            <span className="dropdown-item-title">Upgrade to Premium ★</span>
                            <span className="dropdown-item-desc">Unlock exclusive features</span>
                          </div>
                        </Link>
                      </div>
                    )}
                    {isAdmin && (
                      <div className="dropdown-section">
                        <div className="dropdown-section-title">Admin</div>
                        <Link to="/admin" className="dropdown-item" onClick={() => setActiveDropdown(null)}>
                          <div className="dropdown-item-content">
                            <span className="dropdown-item-title">🎭 Bot Admin</span>
                            <span className="dropdown-item-desc">Manage bot system</span>
                          </div>
                        </Link>
                      </div>
                    )}
                    <div className="dropdown-divider"></div>
                    <div className="dropdown-item sign-out-item" onClick={() => { handleSignOut(); setActiveDropdown(null); }}>
                      <div className="dropdown-item-content">
                        <span className="dropdown-item-title">Sign Out</span>
                        <span className="dropdown-item-desc">Log out of your account</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </nav>

            <div className="social-separator" aria-hidden="true"></div>

            <div className="social-icons">
              <a href="https://x.com/riftchess" target="_blank" rel="noopener noreferrer" className="social-icon x-twitter" aria-label="RiftChess on X">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932L18.901 1.153ZM17.61 20.644h2.039L6.486 3.24H4.298L17.61 20.644Z"/>
                </svg>
              </a>
              <a href="https://discord.gg/riftchess" target="_blank" rel="noopener noreferrer" className="social-icon discord" aria-label="RiftChess on Discord">
                <svg width="20" height="20" viewBox="0 0 71 55" fill="currentColor">
                  <g clipPath="url(#clip0)">
                    <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5177 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1066 30.1693C30.1066 34.1136 27.28 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7636 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.6986 30.1693C53.6986 34.1136 50.9 37.3253 47.3178 37.3253Z"/>
                  </g>
                  <defs>
                    <clipPath id="clip0">
                      <rect width="71" height="55" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
              </a>
              <a href="https://instagram.com/riftchess" target="_blank" rel="noopener noreferrer" className="social-icon instagram" aria-label="RiftChess on Instagram">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <nav className={`main-nav ${showMobileMenu ? 'mobile-open' : ''}`}>
          <Link to="/" className="nav-item" onClick={() => setShowMobileMenu(false)}>Home</Link>
          <Link to="/tournaments" className="nav-item" onClick={() => setShowMobileMenu(false)}>Tournaments</Link>
          <Link to="/leaderboard" className="nav-item" onClick={() => setShowMobileMenu(false)}>Leaderboard</Link>
          <Link to="/why-riftchess" className="nav-item" onClick={() => setShowMobileMenu(false)}>Why RiftChess?</Link>
          <Link to="/rules" className="nav-item" onClick={() => setShowMobileMenu(false)}>Rules & Guide</Link>
          <Link to="/replay" className="nav-item" onClick={() => setShowMobileMenu(false)}>Replay</Link>
          <Link to="/spectate" className="nav-item" onClick={() => setShowMobileMenu(false)}>Spectate Games</Link>
          <Link to="/friends" className="nav-item" onClick={() => setShowMobileMenu(false)}>Friends</Link>
          <Link to="/notifications" className="nav-item" onClick={() => setShowMobileMenu(false)}>Notifications</Link>
          <Link to="/teams" className="nav-item" onClick={() => setShowMobileMenu(false)}>Teams</Link>
          <Link to="/quests" className="nav-item" onClick={() => setShowMobileMenu(false)}>Quests</Link>
          <Link to="/profile" className="nav-item" onClick={() => setShowMobileMenu(false)}>Profile</Link>
          <Link to="/preferences" className="nav-item" onClick={() => setShowMobileMenu(false)}>Game Preferences</Link>
          <button className="nav-item" onClick={() => { handleSignOut(); setShowMobileMenu(false); }}>
            Sign Out
          </button>
        </nav>
      </header>

      {/* Floating Sound Toggle */}
      <SoundToggle />

      <div className="content-container">
         <Outlet />
      </div>

      {/* Footer - only shown for authenticated users */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-section">
            <div className="footer-logo-section">
              <img src="/logo512.png" alt="RiftChess Logo" className="footer-logo" />
              <div className="footer-text-content">
                <span className="footer-title">RiftChess</span>
                <span className="footer-subtitle">The ultimate chess experience</span>
                <div className="footer-social-icons">
                  <a href="https://x.com/riftchess" target="_blank" rel="noopener noreferrer" className="footer-social-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </a>
                  <a href="https://twitch.tv/riftchessgame" target="_blank" rel="noopener noreferrer" className="footer-social-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
                    </svg>
                  </a>
                  <a href="https://instagram.com/riftchess" target="_blank" rel="noopener noreferrer" className="footer-social-icon">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </a>
                  <a href="https://discord.gg/riftchess" target="_blank" rel="noopener noreferrer" className="footer-social-icon">
                    <svg viewBox="0 0 71 55" fill="currentColor">
                      <g clipPath="url(#clip0)">
                        <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1066 30.1693C30.1066 34.1136 27.28 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7636 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.6986 30.1693C53.6986 34.1136 50.9 37.3253 47.3178 37.3253Z"/>
                      </g>
                      <defs>
                        <clipPath id="clip0">
                          <rect width="71" height="55" fill="white"/>
                        </clipPath>
                      </defs>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="footer-section footer-center">
            <div className="footer-center-links">
              <a href="mailto:contact@riftchess.com" className="footer-center-link">Support</a>
              <a href="/privacy" className="footer-center-link">Privacy Policy</a>
              <a href="/fairplay" className="footer-center-link">Cheating & Fair Play</a>
            </div>
          </div>
          <div className="footer-section">
            <a href="mailto:contact@riftchess.com" className="footer-contact">contact@riftchess.com</a>
            <span className="footer-text">© Copyright 2026 RiftChess.</span>
          </div>
        </div>
      </footer>

      {showQuestModal && (
        <QuestAccountModal
          onClose={() => setShowQuestModal(false)}
          onCreateAccount={handleNavigateToAuth}
        />
      )}
    </div>
  )
}
